import { shallowRef, type Ref } from 'vue';

/**
 * Field identifiers held for a short while after something happened to the field (ENG-9765): "Updated" on a field an
 * Assign just recalculated, and the fade on a Follow-up that just appeared. Held outside `form.items`, as the reveal
 * blink is (docs/memory/entry-field-navigation.md), so the deep watch there and the save body never see them, and a
 * row the virtual list mounts again inside the duration still draws the mark.
 */
export interface TransientMarks {
  /** The fields marked now. A new set on every change. */
  readonly ids: Readonly<Ref<ReadonlySet<string>>>;
  /** Marks `identifier` for the duration, starting it again if the field is marked already. */
  mark(identifier: string): void;
  /** Lets every field go, as opening another entry does. */
  clear(): void;
}

export const createTransientMarks = (duration: number): TransientMarks => {
  const ids = shallowRef<ReadonlySet<string>>(new Set());
  const timers = new Map<string, ReturnType<typeof setTimeout>>();

  const release = (identifier: string) => {
    timers.delete(identifier);
    const next = new Set(ids.value);
    next.delete(identifier);
    ids.value = next;
  };

  return {
    ids,
    mark: identifier => {
      clearTimeout(timers.get(identifier));
      timers.set(identifier, setTimeout(() => release(identifier), duration));
      if (!ids.value.has(identifier)) {
        ids.value = new Set(ids.value).add(identifier);
      }
    },
    clear: () => {
      timers.forEach(timer => clearTimeout(timer));
      timers.clear();
      ids.value = new Set();
    }
  };
};
